import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { map, Observable, of, tap } from 'rxjs';
import {
  AdminActions,
  AdminOrganization,
  AdminOrganizationListItem,
  AdminOverview,
  AdminStateModel,
  AdminUser,
  AdminUserListItem,
  ToastActions,
} from '@app/_store';
import { AdminService } from '@app/_services';
import { patch, removeItem } from '@ngxs/store/operators';
import { Navigate } from '@ngxs/router-plugin';

const defaults = {
  overview: undefined,
  organizations: [],
  users: [],
  user: undefined,
  organization: undefined,
};

@State<AdminStateModel>({
  name: 'admin',
  defaults,
})
@Injectable()
export class AdminState {
  constructor(private adminService: AdminService) {}

  @Selector()
  static overview(state: AdminStateModel): AdminOverview {
    return state.overview;
  }

  @Selector()
  static organizations(state: AdminStateModel): AdminOrganizationListItem[] {
    return state.organizations;
  }

  @Selector()
  static organization(state: AdminStateModel): AdminOrganization {
    return state.organization;
  }

  @Selector()
  static users(state: AdminStateModel): AdminUserListItem[] {
    return state.users;
  }

  @Selector()
  static user(state: AdminStateModel): AdminUser {
    return state.user;
  }

  @Action(AdminActions.LoadOverview)
  loadOverview(ctx: StateContext<AdminStateModel>): Observable<void> {
    return this.adminService.getOverview().pipe(
      map(response => {
        ctx.patchState({
          overview: response,
        });
      })
    );
  }

  @Action(AdminActions.LoadOrganizations)
  loadOrganizations(ctx: StateContext<AdminStateModel>): Observable<void> {
    return this.adminService.getOrganizations().pipe(
      map(response => {
        ctx.patchState({
          organizations: response.organizations.map(org => {
            org.lastBilled = org.lastBilled ? new Date(org.lastBilled) : org.lastBilled;
            return org;
          }),
        });
      })
    );
  }

  @Action(AdminActions.LoadOrganization)
  loadOrganization(ctx: StateContext<AdminStateModel>, action: AdminActions.LoadOrganization): Observable<void> {
    if (!action.id) {
      ctx.patchState({
        organization: undefined,
      });
      return of(null);
    } else {
      return this.adminService.getOrganization(action.id).pipe(
        map(organization => {
          organization.administrators = organization.administrators.map(a => {
            a.lastLogin = new Date(a.lastLogin);
            return a;
          });
          ctx.patchState({
            organization: organization,
          });
        })
      );
    }
  }

  @Action(AdminActions.PatchOrganization)
  patchOrganization(ctx: StateContext<AdminStateModel>, action: AdminActions.PatchOrganization): Observable<void> {
    if (!action.patchOperations || action.patchOperations.length === 0) return of(null);

    const id = ctx.getState().organization.id;
    return this.adminService.patchOrganization(id, action.patchOperations).pipe(
      map(organization => {
        organization.administrators = organization.administrators.map(a => {
          a.lastLogin = new Date(a.lastLogin);
          return a;
        });
        ctx.patchState({
          organization: organization,
        });
      }),
      tap(() => ctx.dispatch(new ToastActions.ShowSuccess('Organisationen har uppdaterats!')))
    );
  }

  @Action(AdminActions.LoadUsers)
  loadUsers(ctx: StateContext<AdminStateModel>): Observable<void> {
    return this.adminService.getUsers().pipe(
      map(response => {
        ctx.patchState({
          users: response.users.map(user => {
            user.lastLogin = new Date(user.lastLogin);
            return user;
          }),
        });
      })
    );
  }

  @Action(AdminActions.LoadUser)
  loadUser(ctx: StateContext<AdminStateModel>, action: AdminActions.LoadUser): Observable<void> {
    if (!action.id) {
      ctx.patchState({
        user: undefined,
      });
      return of(null);
    } else {
      return this.adminService.getUser(action.id).pipe(
        map(response => {
          response.lastLogin = new Date(response.lastLogin);
          ctx.patchState({
            user: response,
          });
        })
      );
    }
  }

  @Action(AdminActions.RemoveUserFromOrganization)
  removeUserFromOrganization(
    ctx: StateContext<AdminStateModel>,
    action: AdminActions.RemoveUserFromOrganization
  ): Observable<void> {
    return this.adminService.removeUserFromOrganization(action.user.id, action.organization.id).pipe(
      tap(() => {
        ctx.setState(
          patch<AdminStateModel>({
            organization: patch({
              administrators: removeItem(
                x => x.id == action.user.id && x.fromParentOrganization == action.user.fromParentOrganization
              ),
            }),
          })
        );
      }),
      tap(() => {
        ctx.dispatch(
          new ToastActions.ShowSuccess(`${action.user.name} har tagits bort som administratör från ${action.organization.name}`)
        );
      })
    );
  }

  @Action(AdminActions.DeleteUser)
  deleteUser(ctx: StateContext<AdminStateModel>, action: AdminActions.DeleteUser): Observable<void> {
    return this.adminService.deleteUser(action.userId).pipe(
      tap(() => {
        ctx.setState(
          patch<AdminStateModel>({
            users: removeItem(x => x.id == action.userId),
            user: undefined,
          })
        );
      }),
      tap(() => {
        ctx.dispatch(new Navigate(['admin', 'users']));
      }),
      tap(() => {
        ctx.dispatch(new ToastActions.ShowSuccess(`${action.name} har tagits bort`));
      })
    );
  }
}
